import { Box, Button, Grid } from '@mui/material'
import React, { useEffect, useState } from 'react'
import EmptyTableRow from 'src/Component/EmptyTableRow'
import PaginatedTable, { HeadCellI, SortOrderI } from 'src/Component/PaginatedTable'
import DataRow from './DataRow'
import { useGetPostsListQuery, useDeletePostMutation } from 'src/api/postsApi'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'src/store'
import { startPageLoader, stopPageLoader } from 'src/store/features/common/commonSlice'
import SimpleDropdown from 'src/Component/SimpleDropdown'
import ViewPostDetails from './ViewPostDetails'
import ConfirmationBox from 'src/Component/ConfirmationBox'
import { errorToast, successToast } from 'src/utils/toastService'
import SearchInput from 'src/Component/SearchInput'
import { useNavigate } from 'react-router-dom'

const headCells: HeadCellI[] = [
  {
    id: 'id',
    isCheckBox: true,
    sortable: false,
    label: 'checkbox',
  },
  {
    id: 'posted_time',
    sortable: true,
    label: 'Date',
  },
  {
    id: 'caption',
    sortable: true,
    label: 'Post',
  },
  {
    id: 'posted_by',
    sortable: true,
    label: 'Author',
  },
  {
    id: 'comments_count',
    sortable: true,
    label: 'Comments',
  },
  {
    id: 'id',
    sortable: false,
    label: 'Action',
    align: 'center',
  },
]

const Posts = () => {
  const [page, setPage] = useState<number>(1)
  const [sortOrder, setSortOrder] = useState<SortOrderI>('asc')
  const [sortOrderBy, setSortOrderBy] = useState<string>('') // headCells[i].id
  const [postDetailsModal, setPostDetailsModal] = useState({
    id: 0,
    open: false,
  })

  const [postDeleteConfirmation, setPostDeleteConfirmation] = useState({
    id: 0,
    open: false,
  })

  const [selectedIds, setSelectedPostIds] = useState<number[]>([])
  const [search, setSearch] = useState<string>('')

  // hooks
  const {
    data: POSTS_DATA_RESPONSE,
    isFetching,
    refetch,
  } = useGetPostsListQuery(
    {
      search,
      page,
      pageSize: 10,
      sortBy: (() => {
        if (sortOrder === 'asc') {
          return sortOrderBy
        } else {
          return `-${sortOrderBy}`
        }
      })(),
    },
    { refetchOnMountOrArgChange: true },
  )
  const [deletePost, { isLoading }] = useDeletePostMutation()
  const dispatch = useDispatch<AppDispatch>()

  // functions
  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const handleSorting = (_sortOrderBy: string) => {
    setSortOrder((prevState) => {
      return prevState === 'asc' ? 'desc' : 'asc'
    })
    setSortOrderBy(_sortOrderBy)
  }

  const handlepostDetailsModalClose = () => {
    setPostDetailsModal({
      id: 0,
      open: false,
    })
  }

  const handleViewDetailsClick = (id: number) => {
    setPostDetailsModal({
      id: id,
      open: true,
    })
  }

  const handleCloseDeleteConfirmation = () => {
    setPostDeleteConfirmation({
      id: 0,
      open: false,
    })
  }

  const handleOpenDeleteConfirmation = (id: number) => {
    setPostDeleteConfirmation({
      id: id,
      open: true,
    })
  }

  const handleDeletePost = () => {
    deletePost(postDeleteConfirmation.id)
      .unwrap()
      .then((res) => {
        console.log(res)
        successToast('Post deleted successfully.')
        refetch()
      })
      .catch((err) => {
        console.log(err)
        errorToast('Unable to delete post.')
      })
      .finally(() => {
        handleCloseDeleteConfirmation()
        setPostDetailsModal({
          id: 0,
          open: false,
        })
      })
  }

  const handleSelectPost = (id: number) => {
    if (selectedIds.includes(id)) {
      setSelectedPostIds((prevState) => {
        return prevState.filter((postId) => postId !== id)
      })
    } else {
      setSelectedPostIds((prevState) => {
        return [...prevState, id]
      })
    }
  }

  const handleSelectAllClick = (checked: boolean) => {
    if (checked && POSTS_DATA_RESPONSE?.results.length) {
      setSelectedPostIds(POSTS_DATA_RESPONSE.results.map((post) => post.id))
    } else {
      setSelectedPostIds([])
    }
  }

  const handleDeleteSelected = () => {
    Promise.all(selectedIds.map((id) => deletePost(id).unwrap()))
      .then((res) => {
        console.log(res)
        successToast('Selected post deleted successfully.')
        refetch()
      })
      .catch((err) => {
        console.log(err)
        errorToast('Unable to delete selected posts.')
      })
  }

  const handleSearch = (value: string) => {
    setPage(1)
    setSearch(value)
  }

  // use effects
  useEffect(() => {
    if (isFetching || isLoading) {
      dispatch(startPageLoader())
    } else {
      dispatch(stopPageLoader())
    }
  }, [isFetching, isLoading])

  useEffect(() => {
    setSelectedPostIds([])
  }, [POSTS_DATA_RESPONSE])

  return (
    <Box className='pageContainer'>
      <Grid container>
        <Grid md={8} sm={12} xs={12}>
          {' '}
        </Grid>
        <Grid md={4} sm={12} xs={12}>
          <Box>
            <SearchInput hasDebounce onChange={handleSearch} />
          </Box>
        </Grid>
      </Grid>

      <Box mt={2}>
        {POSTS_DATA_RESPONSE && (
          <PaginatedTable
            title='Posts'
            headCells={headCells}
            currentPage={page}
            onPageChange={handlePageChange}
            totalPages={Math.ceil(POSTS_DATA_RESPONSE?.count / 10)}
            onSortClick={handleSorting}
            onSelectAllClick={handleSelectAllClick}
            isAllSelected={POSTS_DATA_RESPONSE.results.length === selectedIds.length}
            numSelected={selectedIds.length}
            onDeleteSelected={handleDeleteSelected}
          >
            <>
              {POSTS_DATA_RESPONSE?.results?.length === 0 && (
                <EmptyTableRow cellCount={headCells.length} />
              )}
              {POSTS_DATA_RESPONSE?.results?.map((post) => {
                return (
                  <DataRow
                    key={post.id}
                    id={post.id}
                    date={post.posted_at_view}
                    title={post.caption}
                    name={`${post.posted_by.first_name} ${post.posted_by.last_name}`}
                    commentsCount={post.comments_count}
                    selected={selectedIds.includes(post.id)}
                    onViewDetailsClick={handleViewDetailsClick}
                    onDeleteClick={handleOpenDeleteConfirmation}
                    onSelect={handleSelectPost}
                  />
                )
              })}
            </>
          </PaginatedTable>
        )}
      </Box>

      {!!postDetailsModal.id && (
        <ViewPostDetails
          id={postDetailsModal.id}
          open={postDetailsModal.open}
          onClose={handlepostDetailsModalClose}
          onDeleteClick={handleOpenDeleteConfirmation}
        />
      )}

      <ConfirmationBox
        title='Are you sure you want to Delete this post.'
        open={postDeleteConfirmation.open}
        onReject={handleCloseDeleteConfirmation}
        onAccept={handleDeletePost}
      />
    </Box>
  )
}

export default Posts
